/**
 * Client-side mirror of the backend's `canonical_key` helper
 * (web-app/backend/app/services/fetchers/registry.py).
 *
 * Produces a stable identifier for a listing URL so the Compare
 * input can detect duplicates BEFORE hitting the backend - paste the
 * same listing in two slots, via its SEO path vs its /items/:id
 * canonical URL, or with a tracking query string, and they'll
 * collapse to the same key.
 *
 * Returns null for:
 *   - URLs that don't parse
 *   - domains we don't route to a fetcher
 *   - supported domains where the id regex doesn't match
 *
 * For "is this the same listing?" the key is the source of truth.
 * Backend still re-checks on submit, so this is purely UX - the user
 * sees a duplicate warning as they type rather than at submit time.
 */
/**
 * The listing sources we route to a live fetcher, as bare hostnames.
 * Single source of truth for the frontend - every place that lists or
 * matches supported sites (preview pre-flight, clipboard/share URL
 * extraction, the "supported sites" chip row) derives from this so the
 * set can't silently drift to "bina.az + emlak.az only" again. Mirrors
 * the backend fetcher registry's aggregated domains.
 */
export const SUPPORTED_LISTING_DOMAINS = [
  "bina.az",
  "emlak.az",
  "ev10.az",
  "arenda.az",
  "tap.az",
  "lalafo.az",
  "yeniemlak.az",
  "emlaktap.az",
] as const;

const _DOMAIN_ALT = SUPPORTED_LISTING_DOMAINS.map((d) => d.replace(/\./g, "\\.")).join("|");

/**
 * Matches a supported listing URL anywhere in free text. Mirrors the
 * backend's _URL_RE in telegram_bot.py so every entry point (clipboard
 * paste, share-target payload, /analyze extractor) recognises the same
 * format. Built from SUPPORTED_LISTING_DOMAINS so adding a fetcher is a
 * one-line change.
 */
export const LISTING_URL_RE = new RegExp(
  `https?://(?:www\\.)?(?:${_DOMAIN_ALT})/[^\\s]+`,
  "i",
);

/**
 * Pull the first supported listing URL out of a free-text payload
 * (clipboard contents, a shared message), stripping trailing
 * punctuation users / chat apps tack on so the backend fetcher doesn't
 * 404 on "bina.az/items/123.". Returns null when no supported URL is
 * present.
 */
export function extractListingUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const match = raw.match(LISTING_URL_RE);
  if (!match) return null;
  return match[0].replace(/[.,);\]!?'"»]+$/, "");
}

/**
 * Cheap pre-flight for the live preview card: does this look like a
 * supported listing URL worth a backend round-trip? Avoids a network
 * call on every keystroke while the user is mid-typing. The backend
 * still rejects unknown domains - this is purely an optimisation.
 */
/** True when the text is a link of any kind (a listing link, or a URL
 *  we do not support) rather than words to search for. The homepage
 *  box takes both (owner, 2026-10-04: search is primary, the link is
 *  the second way in). */
export function looksLikeLink(raw: string): boolean {
  const v = raw.trim();
  return /^(https?:\/\/|www\.)/i.test(v) || /^[a-z0-9.-]+\.(az|com|ru|net|org|ai)(\/|$)/i.test(v) || looksLikeListingUrl(v);
}

/** Where a home search bar sends a link (owner, 2026-10-29: "When we
 *  present a link, it should go and try to evaluate it"): one of our own
 *  listing pages opens as it is (it carries the valuation); any other link
 *  goes to the valuation, /analyze?url=, which reads a listing site's page
 *  or, through the intake, any other page. Null for words, or a link with
 *  words around it (that is a search). */
export function evaluationHref(raw: string | null | undefined): string | null {
  const v = (raw ?? "").trim().replace(/[.,);\]!?'"»]+$/, "");
  if (!v || /\s/.test(v) || !looksLikeLink(v)) return null;
  const own = /^(?:https?:\/\/)?(?:www\.)?(?:menzil\.ai|localhost(?::\d+)?)\/(?:(?:az|ru|en)\/)?(elan|l)\/([^/?#\s]+)/i.exec(v);
  if (own) return `/${own[1]}/${own[2]}`;
  const url = /^https?:\/\//i.test(v) ? v : `https://${v}`;
  return `/analyze?url=${encodeURIComponent(url)}`;
}

export function looksLikeListingUrl(raw: string): boolean {
  const v = raw.trim();
  if (v.length < 18) return false;
  if (!/^https?:\/\//i.test(v)) return false;
  return LISTING_URL_RE.test(v);
}

/**
 * The link to a page on one of the listing sites that the whole input
 * is, as a full URL (scheme added, trailing punctuation dropped), or
 * null. The search bars use it to tell a link to look up from words to
 * search (owner, 2026-10-17: "if I paste the link into the search bar,
 * I want it to be identified"). A link to another site, a site's home
 * page, two links, or a link with words around it stays text.
 */
/** Our own listing pages, menzil.ai/<locale>/elan/<site>-<id>, and the
 *  list and map links that open one (?open=bina_az:6535785) (owner,
 *  2026-10-17: "we should also support pasting our own links"). The
 *  page's own host counts too, so a local preview's links work. */
function isOwnListingLink(u: URL): boolean {
  const host = u.hostname.toLowerCase().replace(/^www\./, "");
  const here = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  if (host !== "menzil.ai" && host !== here) return false;
  if (/^\/(?:az|ru|en)\/elan\/[a-z0-9]+-[0-9A-Za-z_-]{1,40}\/?$/.test(u.pathname)) return true;
  return /^[a-z0-9]+_az:[0-9A-Za-z_-]{1,40}$/.test(u.searchParams.get("open") ?? "");
}

export function listingLinkOf(raw: string | null | undefined): string | null {
  const v = (raw ?? "").trim().replace(/[.,);\]!?'"»]+$/, "");
  if (!v || /\s/.test(v) || (v.match(/https?:\/\//gi) || []).length > 1) return null;
  if (!looksLikeLink(v)) return null;
  let u: URL;
  try {
    u = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`);
  } catch {
    return null;
  }
  if (isOwnListingLink(u)) return u.href;
  const host = u.hostname.toLowerCase().replace(/^(?:www|m|ru|en|az)\./, "");
  if (!(SUPPORTED_LISTING_DOMAINS as readonly string[]).includes(host)) return null;
  if (u.pathname.replace(/\/+$/, "") === "") return null;
  return u.href;
}

/** The listing_index id of a listing link: canonicalListingKey's
 *  `<host>:<id>` (`bina.az:123`) with the host's dots as underscores
 *  (`bina_az:123`), the id the watchlist and the report take. Null when
 *  the link is not a listing of a known site. */
export function indexIdFromUrl(url?: string | null): string | null {
  if (!url) return null;
  const key = canonicalListingKey(url);
  if (!key) return null;
  const i = key.indexOf(":");
  if (i <= 0) return null;
  return key.slice(0, i).replace(/\./g, "_") + key.slice(i);
}

export function canonicalListingKey(raw: string): string | null {
  if (!raw) return null;
  const input = raw.trim();
  if (!input) return null;
  // Exactly ONE URL per value. `new URL` percent-encodes interior
  // whitespace instead of throwing, so "link link" (the same URL
  // pasted twice into one box) would otherwise parse as a single
  // valid URL keyed off the first id match. Whitespace after trim,
  // or a second scheme occurrence, means the value is not a URL.
  if (/\s/.test(input)) return null;
  if ((input.match(/https?:\/\//gi) || []).length > 1) return null;
  let u: URL;
  try {
    u = new URL(input.includes("://") ? input : `https://${input}`);
  } catch {
    return null;
  }
  // Collapse the known single-label prefixes (`www.`, `m.`, and the
  // `ru.` / `en.` / `az.` locale variants bina.az serves) so the
  // same listing pasted from any locale subdomain lands on one
  // canonical key. Must stay in sync with the backend's _PREFIX_RE
  // in registry.py.
  const host = u.hostname.toLowerCase().replace(/^(?:www|m|ru|en|az)\./, "");
  if (host === "bina.az") {
    // bina.az listing IDs are always the last ≥4-digit numeric segment
    // of the path. Handles /items/12345, /baki/menziller/yeni-tikili/12345,
    // and similar SEO paths.
    const m = u.pathname.match(/\/(\d{4,})/);
    return m ? `bina.az:${m[1]}` : null;
  }
  if (host === "tap.az") {
    // tap.az: /elanlar/dasinmaz-emlak/<kind>/<id>; the id is the last
    // numeric segment (mirrors the backend fetcher).
    const m = u.pathname.match(/\/(\d{5,})(?:\/|$)/);
    return m ? `tap.az:${m[1]}` : null;
  }
  if (host === "lalafo.az") {
    // lalafo.az: /<city>/ads/<slug>-id-<id>; a bare numeric id also works.
    const m = u.pathname.match(/-id-(\d+)(?:\/|$)/) || u.pathname.match(/\/(\d{6,})(?:\/|$)/);
    return m ? `lalafo.az:${m[1]}` : null;
  }
  if (host === "yeniemlak.az") {
    // yeniemlak.az: /elan/<slug>-<id> or /elan/<id>; the slug is
    // decorative, the trailing number is the id (mirrors the backend's
    // extract_listing_id). /elan/axtar (the search) has no number.
    const m = u.pathname.match(/^\/elan\/(?:[a-z0-9-]*?-)?(\d{1,9})\/?$/i);
    return m ? `yeniemlak.az:${m[1]}` : null;
  }
  if (host === "emlaktap.az") {
    // emlaktap.az: /emlak/<slug>-<id>/ or /property/<id>/ (the site's
    // redirect to the slug page); the trailing number is the id, as in
    // the backend's extract_listing_id. Category pages end in none.
    const m = u.pathname.match(/^\/(?:emlak|property)\/(?:[a-z0-9-]*?-)?(\d{1,9})\/?$/i);
    return m ? `emlaktap.az:${m[1]}` : null;
  }
  if (host === "emlak.az") {
    // emlak.az URLs: /12345-foo-bar-baz.html
    const m = u.pathname.match(/^\/(\d+)-.*\.html$/i);
    return m ? `emlak.az:${m[1]}` : null;
  }
  if (host === "ev10.az") {
    // ev10.az id from /posting/<id>, then any standalone 4+ digit path
    // segment (covers bare /<id> + most SEO URLs). Mirrors the backend's
    // _extract_url_id; a SEO slug with no id segment yields null here and
    // the fetch path resolves the real id from the payload.
    const m = u.pathname.match(/\/posting\/(\d+)/i) || u.pathname.match(/\/(\d{4,})(?:\/|$)/);
    return m ? `ev10.az:${m[1]}` : null;
  }
  if (host === "arenda.az") {
    // arenda.az detail URLs are root-level SEO slugs. Some end in the
    // elan id ("...-451234"), many do NOT (the real id is in the page
    // body, which the backend reads). Mirror the backend is_detail_slug:
    // known prefix + (trailing number OR a verbose slug with >= 5 hyphens
    // - long enough to be a real listing, not a short category page like
    // "satilir-menziller"). We key on the slug itself.
    const path = u.pathname.replace(/^\/+|\/+$/g, "");
    const hyphens = (path.match(/-/g) || []).length;
    if (
      path &&
      !path.includes("/") &&
      /^(satilir|kiraye-ayliq|kiraye-gunluk|kiraye)-/.test(path) &&
      (/-\d+$/.test(path) || hyphens >= 5)
    ) {
      return `arenda.az:${path}`;
    }
    return null;
  }
  return null;
}

/** Underscored source code (as stored in listing_index.source) -> host. */
const _SOURCE_CODE_HOST: Record<string, string> = {
  bina_az: "bina.az",
  emlak_az: "emlak.az",
  ev10_az: "ev10.az",
  arenda_az: "arenda.az",
  tap_az: "tap.az",
  lalafo_az: "lalafo.az",
  yeniemlak_az: "yeniemlak.az",
  emlaktap_az: "emlaktap.az",
};

/**
 * Display host for a listing's source badge ("bina.az", "emlak.az",
 * "ev10.az", "arenda.az"). Derived from the real source URL so the badge
 * always matches where the link actually points - never hardcode it now
 * that the index is multi-source. Falls back to the source code
 * ("ev10_az" -> "ev10.az") when no URL is available.
 */
export function sourceHostLabel(
  sourceUrl?: string | null,
  source?: string | null,
): string {
  if (sourceUrl) {
    try {
      const host = new URL(sourceUrl).hostname.toLowerCase().replace(/^(?:www|m|ru|en|az)\./, "");
      if (host) return host;
    } catch {
      /* fall through to the source-code map */
    }
  }
  const code = (source ?? "").toLowerCase();
  return _SOURCE_CODE_HOST[code] ?? (code ? code.replace(/_/g, ".") : "bina.az");
}

/** A pasted link that points at an image file rather than a page: the
 *  path ends in an image extension (a query string may follow), or it is
 *  a listing photo on a source CDN. Such a link is searched by look. */
export function looksLikeImageLink(raw: string): boolean {
  const v = raw.trim();
  if (!/^https?:\/\//i.test(v) || /\s/.test(v)) return false;
  let u: URL;
  try { u = new URL(v); } catch { return false; }
  if (/\.(jpe?g|png|webp|gif|bmp|heic|heif|avif)$/i.test(u.pathname)) return true;
  return /(^|\.)azstatic\.com$/i.test(u.hostname) && /\/uploads\//.test(u.pathname);
}
